import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

const SECTIONS = [
  { id: "introduction", label: "1. Introduction" },
  { id: "controller", label: "2. Data controller" },
  { id: "data-we-collect", label: "3. Data we collect" },
  { id: "purposes", label: "4. How we use data" },
  { id: "lawful-basis", label: "5. Lawful basis" },
  { id: "third-parties", label: "6. Third-party processors" },
  { id: "storage", label: "7. Storage & security" },
  { id: "ai-models", label: "8. AI processing" },
  { id: "google-services", label: "9. Google APIs & Limited Use" },
  { id: "retention", label: "10. Retention" },
  { id: "cookies", label: "11. Cookies & analytics" },
  { id: "your-rights", label: "12. Your rights" },
  { id: "transfers", label: "13. International transfers" },
  { id: "children", label: "14. Children" },
  { id: "changes", label: "15. Changes" },
  { id: "contact", label: "16. Contact" },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How AgentFlow Ventures Ltd. collects, uses and protects your information when you use AgentFlow Compliance."
      lastUpdated="February 23, 2026"
      sections={SECTIONS}
    >
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy explains how{" "}
          <strong>AgentFlow Ventures Ltd.</strong> ("AgentFlow", "we", "us",
          "our") collects, uses, discloses and protects personal data when you
          use our website, applications and related services (the
          "Service"). It applies to all visitors and customers regardless of
          jurisdiction. We have written it to meet the spirit of leading
          privacy frameworks including the EU GDPR, the UK GDPR and the CCPA.
        </p>
      </section>

      <section id="controller">
        <h2>2. Data controller</h2>
        <p>
          The data controller for personal data processed through the Service is:
        </p>
        <p>
          <strong>AgentFlow Ventures Ltd.</strong><br />
          Cayman Islands<br />
          Contact: <a href="mailto:support@agentflow.ventures">support@agentflow.ventures</a>
        </p>
      </section>

      <section id="data-we-collect">
        <h2>3. Data we collect</h2>
        <h3>Information you provide directly</h3>
        <ul>
          <li><strong>Account data</strong>: email address, name, password hash, profile preferences.</li>
          <li><strong>Workspace data</strong>: workspace name, team-member emails and roles.</li>
          <li><strong>Customer Content</strong>: documents, contracts, policies and free-form text you upload or paste into the Service for scanning.</li>
          <li><strong>Communications</strong>: messages you send to our support team.</li>
          <li><strong>Marketing data</strong>: information you enter on our waitlist or free risk-score lead form (email, business type, employee count).</li>
        </ul>
        <h3>Information collected automatically</h3>
        <ul>
          <li><strong>Usage data</strong>: pages viewed, features used, error logs, request timestamps.</li>
          <li><strong>Technical data</strong>: IP address, browser type and version, operating system, device identifiers.</li>
          <li><strong>Cookies</strong>: see section 10.</li>
        </ul>
        <h3>Information from third parties</h3>
        <ul>
          <li><strong>Payments</strong>: limited information from our payment processor (last four digits of card, transaction status, country). We never see or store full card numbers.</li>
          <li><strong>Authentication</strong>: if you sign in with a third-party identity provider, that provider may share basic profile information with us.</li>
        </ul>
      </section>

      <section id="purposes">
        <h2>4. How we use data</h2>
        <p>We use personal data to:</p>
        <ul>
          <li>operate, deliver and improve the Service;</li>
          <li>authenticate users and prevent fraud and abuse;</li>
          <li>process payments and manage subscriptions;</li>
          <li>generate AI compliance scans, summaries and reports for your account;</li>
          <li>send transactional emails (login links, scan alerts, weekly digests);</li>
          <li>respond to support requests;</li>
          <li>send marketing emails to people who have opted in (you can unsubscribe at any time);</li>
          <li>comply with legal obligations and enforce our agreements.</li>
        </ul>
      </section>

      <section id="lawful-basis">
        <h2>5. Lawful basis (GDPR)</h2>
        <p>Where GDPR applies we rely on the following lawful bases:</p>
        <ul>
          <li><strong>Contract</strong>: to provide the Service you have purchased.</li>
          <li><strong>Legitimate interests</strong>: to secure the Service, prevent fraud, improve features and contact existing customers about product updates.</li>
          <li><strong>Consent</strong>: for marketing emails to non-customers and for non-essential cookies. You can withdraw consent at any time.</li>
          <li><strong>Legal obligation</strong>: to retain tax and billing records.</li>
        </ul>
      </section>

      <section id="third-parties">
        <h2>6. Third-party processors</h2>
        <p>
          We share personal data with carefully selected third-party
          processors strictly to deliver the Service. Categories include:
        </p>
        <ul>
          <li><strong>Cloud hosting</strong> — to run our application servers and databases.</li>
          <li><strong>AI model providers</strong> — to generate compliance analyses; document text is sent to the model provider for inference only and is not retained by them for training.</li>
          <li><strong>Email delivery</strong> — to send transactional and (where consented) marketing emails.</li>
          <li><strong>Payment processing</strong> — currently <strong>Paddle.com</strong>, who acts as the merchant of record and handles all card data.</li>
          <li><strong>Analytics & error monitoring</strong> — to track usage in aggregate and diagnose errors.</li>
        </ul>
        <p>
          All processors are bound by written agreements that require
          appropriate technical and organisational safeguards.
        </p>
      </section>

      <section id="storage">
        <h2>7. Storage & security</h2>
        <p>
          We host the Service on reputable cloud infrastructure providers
          using global infrastructure. Personal data is encrypted in transit
          (TLS) and at rest (AES-256). We restrict access on a need-to-know
          basis, require strong authentication for our team, log access, and
          continuously monitor for security events. No security model is
          perfect; if we ever experience a breach affecting your data we will
          notify you and the relevant authorities as required by law.
        </p>
      </section>

      <section id="ai-models">
        <h2>8. AI processing</h2>
        <p>
          Documents you submit for scanning are sent to a large-language-model
          provider purely to perform that scan. We have configured the
          integration so that your content is not used by the provider to
          train shared foundation models. Outputs (risk scores, summaries,
          recommended fixes) are stored in your workspace for your reference
          and may be aggregated, in fully de-identified form, to improve
          AgentFlow's own product quality.
        </p>
      </section>

      <section id="google-services">
        <h2>9. Google APIs &amp; Limited Use</h2>
        <p>
          AgentFlow Compliance offers an optional integration with{" "}
          <strong>Google Drive</strong> so that workspace users can pick
          contracts and policies from their Drive and import them into
          AgentFlow for scanning. This integration uses the Google OAuth 2.0
          flow and the Google Drive API.
        </p>
        <h3>What scopes we request</h3>
        <ul>
          <li>
            <strong>
              <code>https://www.googleapis.com/auth/drive.file</code>
            </strong>{" "}
            — read access limited to the specific files you explicitly
            select via the Google Picker. We do <em>not</em> request access
            to your entire Drive, your folders, or files you have not
            selected.
          </li>
        </ul>
        <h3>How we use the data we receive</h3>
        <ul>
          <li>
            We download the file contents you select, extract the text
            (using the same pipeline as direct uploads), and store the
            extracted text as a Document inside your AgentFlow workspace.
          </li>
          <li>
            We do <strong>not</strong> read, scan, index or share any other
            file in your Drive. We do not enumerate your Drive structure.
          </li>
          <li>
            We do <strong>not</strong> use Google user data to train,
            improve or otherwise develop machine-learning models, including
            our own. Document text sent to AI model providers for analysis
            is used only to generate that scan's output.
          </li>
          <li>
            We do <strong>not</strong> sell, rent or transfer Google user
            data to data brokers, advertising networks, or any party for
            advertising, credit-worthiness, or any other purpose unrelated
            to providing AgentFlow Compliance to you.
          </li>
          <li>
            Human review of Google user data is limited to: (a) with your
            explicit consent, (b) to comply with applicable law, (c) for
            security investigations, or (d) where the data has been
            aggregated and anonymised.
          </li>
        </ul>
        <h3>Storage &amp; revocation</h3>
        <p>
          OAuth refresh tokens are stored encrypted at rest. You can
          disconnect Google Drive at any time from{" "}
          <strong>Settings → Connected accounts</strong>; doing so revokes
          our token with Google and deletes the credential from our
          database. Any Documents already imported remain in your
          workspace as if you had uploaded them directly.
        </p>
        <p>
          AgentFlow's use and transfer of information received from Google
          APIs to any other app will adhere to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the <strong>Limited Use</strong> requirements.
        </p>
      </section>

      <section id="retention">
        <h2>10. Retention</h2>
        <ul>
          <li><strong>Account data</strong>: kept while your account is active and for up to 24 months after deletion to satisfy legal and audit obligations.</li>
          <li><strong>Customer Content</strong>: kept while your account is active. You can delete individual documents at any time. On account deletion we remove your content from production systems within 30 days; backups roll off within 60 days.</li>
          <li><strong>Billing records</strong>: kept for up to 10 years to satisfy tax and accounting laws.</li>
          <li><strong>Marketing data</strong>: kept until you unsubscribe or 36 months of inactivity.</li>
        </ul>
      </section>

      <section id="cookies">
        <h2>11. Cookies & analytics</h2>
        <p>
          We use a small number of cookies: a session cookie to keep you
          logged in (strictly necessary), and aggregate analytics cookies to
          understand which features are used. Where required by law we ask
          for consent before setting non-essential cookies. You can clear or
          disable cookies in your browser settings; this may break parts of
          the Service.
        </p>
      </section>

      <section id="your-rights">
        <h2>12. Your rights</h2>
        <p>
          Depending on your location you may have the right to: access the
          personal data we hold about you; correct inaccurate data; request
          deletion; object to or restrict processing; receive a portable
          copy; and lodge a complaint with a supervisory authority. To
          exercise any of these rights email{" "}
          <a href="mailto:support@agentflow.ventures">support@agentflow.ventures</a>{" "}
          and we will respond within 30 days. We will not discriminate
          against you for exercising your rights.
        </p>
      </section>

      <section id="transfers">
        <h2>13. International transfers</h2>
        <p>
          AgentFlow operates from the Cayman Islands and uses global
          infrastructure providers. Personal data may be processed in
          jurisdictions other than your own. Where transfers from the
          European Economic Area or the United Kingdom occur, we rely on the
          standard contractual clauses approved by the relevant authorities,
          or on other appropriate transfer mechanisms.
        </p>
      </section>

      <section id="children">
        <h2>14. Children</h2>
        <p>
          The Service is not directed at children under 18 and we do not
          knowingly collect personal data from anyone under 18. If you
          believe a child has provided us personal data, please contact us
          and we will delete it.
        </p>
      </section>

      <section id="changes">
        <h2>15. Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Material
          changes will be notified by email or in-product notice at least
          fourteen (14) days before they take effect. The "Last updated"
          date at the top of this page reflects the latest revision.
        </p>
      </section>

      <section id="contact">
        <h2>16. Contact</h2>
        <p>
          For any privacy questions, requests or complaints, email{" "}
          <a href="mailto:support@agentflow.ventures">support@agentflow.ventures</a>.
        </p>
        <p>
          AgentFlow Ventures Ltd.<br />
          Cayman Islands
        </p>
      </section>
    </LegalLayout>
  );
}
